"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { MainCard, MainCardContent, MainCardHeader, MainCardTitle } from "@/components/ui/main-card";
import { RankingTable } from "@/components/ranking-table";
import { StaggerContainer, StaggerItem, FadeSlideUp } from "@/components/animated-section";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { cn } from "@/lib/utils";

export function AppDetailSkeleton() {
  const { isTerminal, hasGlow, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;
  const showGlow = mounted && hasGlow;

  return (
    <div className={cn(
      "text-foreground mx-auto",
      showTerminal && "bg-black"
    )}>
      <div className="">
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:h-screen lg:overflow-hidden">
          {/* Left content (main app details - takes 2 columns) */}
          <FadeSlideUp delay={0} className="lg:col-span-2 md:p-4 p-4 lg:h-screen lg:overflow-y-auto pb-24">
            <StaggerContainer>
              {/* Header */}
              <StaggerItem className={cn(
                "relative flex items-center gap-4 mb-4",
                showTerminal && "p-4 border border-emerald-500/20 rounded-none",
                showGlow && "shadow-[0_0_8px_rgba(16,185,129,0.1)]"
              )}>
                <div className={cn(
                  "h-32 w-32 rounded-2xl overflow-hidden shadow-sm",
                  !showTerminal && "ring-1 ring-inset ring-black/[0.02] dark:ring-white/[0.02]",
                  showTerminal && "rounded-none border border-emerald-500/30"
                )}>
                  <Skeleton className="h-full w-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-10 w-32 mb-3" />
                  <Skeleton className="h-4 w-64 mb-3" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="absolute right-0 top-0 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded" />
                  <Skeleton className="h-6 w-6 rounded" />
                </div>
              </StaggerItem>

              {/* Metrics (4 separate cards) */}
              <StaggerItem className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <MainCard
                    key={i}
                    className={cn(
                      showTerminal && "border border-emerald-500/20 rounded-none bg-black"
                    )}
                  >
                    {/* Terminal mode corner accents */}
                    {showTerminal && (
                      <>
                        <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-emerald-500/50" />
                        <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-emerald-500/50" />
                        <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-emerald-500/50" />
                        <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-emerald-500/50" />
                      </>
                    )}
                    <MainCardContent>
                      <div className="py-3">
                        <Skeleton className="h-3 w-16 mb-2" />
                        <Skeleton className="h-12 w-24" />
                      </div>
                    </MainCardContent>
                  </MainCard>
                ))}
              </StaggerItem>

              {/* Screenshots */}
              <StaggerItem className="mb-3">
                <div className="flex overflow-x-auto gap-4 min-h-[475px]">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "h-[475px] min-w-[220px] overflow-hidden flex items-center justify-center",
                        showTerminal
                          ? "rounded-none border border-emerald-500/20 bg-black"
                          : "rounded-xl bg-sidebar-lighter border border-black/[0.01] dark:border-white/[0.01]"
                      )}
                    >
                      <Skeleton className="h-full w-full" />
                    </div>
                  ))}
                </div>
              </StaggerItem>

              {/* Description */}
              <StaggerItem>
                <MainCard className={cn(
                  "mb-3",
                  showTerminal && "border border-emerald-500/20 rounded-none bg-black"
                )}>
                  <MainCardHeader>
                    <MainCardTitle className={cn(
                      showTerminal && "text-emerald-500/80 font-mono text-xs uppercase tracking-widest"
                    )}>
                      <Skeleton className="h-4 w-32" />
                    </MainCardTitle>
                  </MainCardHeader>
                  <MainCardContent>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </MainCardContent>
                </MainCard>
              </StaggerItem>

              {/* What's New & Info */}
              <StaggerItem>
                <MainCard className={cn(
                  "mb-3",
                  showTerminal && "border border-emerald-500/20 rounded-none bg-black"
                )}>
                  <MainCardHeader>
                    <MainCardTitle className={cn(
                      showTerminal && "text-emerald-500/80 font-mono text-xs uppercase tracking-widest"
                    )}>
                      <Skeleton className="h-4 w-40" />
                    </MainCardTitle>
                  </MainCardHeader>
                  <MainCardContent>
                    <div className="space-y-4">
                      <Skeleton className="h-16 w-full" showLoadingText />
                      <div className="grid grid-cols-3 gap-4">
                        <Skeleton className="h-6 w-24" />
                        <Skeleton className="h-6 w-24" />
                        <Skeleton className="h-6 w-24" />
                      </div>
                    </div>
                  </MainCardContent>
                </MainCard>
              </StaggerItem>
            </StaggerContainer>
          </FadeSlideUp>

          {/* Right sidebar (competitors + country rankings - takes 1 column) */}
          <FadeSlideUp delay={0.1} className="lg:col-span-1 lg:h-screen lg:overflow-hidden">
            <div className={cn(
              "h-full flex flex-col border-l",
              showTerminal ? "border-emerald-500/20 bg-black" : "border-sidebar-border bg-card"
            )}>
              {/* Top: Competitors */}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                <div className={cn(
                  "p-3 border-b flex-shrink-0 flex items-center justify-between h-[53px] box-border",
                  showTerminal ? "border-emerald-500/20 bg-black/50" : ""
                )}>
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-8 w-8 rounded-md" />
                </div>
                <div className="flex-1 overflow-hidden">
                  <RankingTable
                    data={[]}
                    isLoading={true}
                    showChangeColumn={true}
                    reviewsHeaderStacked
                    nameColClassName="w-28 sm:w-36 md:w-44"
                    showExternalLink={false}
                  />
                </div>
              </div>

              {/* Bottom: Countries */}
              <div className={cn(
                "flex-1 min-h-0 overflow-y-auto flex flex-col border-t",
                showTerminal ? "border-emerald-500/20" : "border-sidebar-border"
              )}>
                <div className={cn(
                  "p-3 border-b flex-shrink-0 flex items-center justify-between h-[53px] box-border",
                  showTerminal ? "border-emerald-500/20 bg-black/50" : ""
                )}>
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className={cn(
                  "flex-1 p-2 flex items-center justify-center overflow-hidden",
                  showTerminal ? "bg-black" : "bg-background"
                )}>
                  {/* Globe Skeleton: 3D effect - terminal mode uses emerald */}
                  <div className={cn(
                    "relative w-[360px] h-[360px] rounded-full animate-pulse",
                    showTerminal
                      ? "bg-[radial-gradient(circle_at_30%_30%,_#064e3b,_#000)] shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.5),_0_0_20px_rgba(16,185,129,0.15)]"
                      : "bg-[radial-gradient(circle_at_30%_30%,_#F3F4F6,_#D1D5DB)] dark:bg-[radial-gradient(circle_at_30%_30%,_#2A2A2A,_#000)] shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.1),_0_0_20px_rgba(249,115,22,0.15)] dark:shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.5),_0_0_20px_rgba(0,210,106,0.1)]"
                  )}>
                    <div className={cn(
                      "absolute inset-0 rounded-full",
                      showTerminal
                        ? "shadow-[0_0_30px_rgba(16,185,129,0.2)]"
                        : "shadow-[0_0_30px_rgba(249,115,22,0.2)] dark:shadow-[0_0_30px_rgba(0,210,106,0.15)]"
                    )} />
                  </div>
                </div>
              </div>
            </div>
          </FadeSlideUp>
        </div>
      </div>
    </div>
  );
}
